import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import { HELP_CLASS } from "../guardias/guardiasShared";
import type { CrmDataProvider } from "../providers/types";
import type { InternalNoticeUser } from "../types";

export const USERS_QUERY_KEY = ["internalNotices", "users"] as const;
const PAGE_SIZE = 100;

const UserRow = ({ user }: { user: InternalNoticeUser }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });

  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      dataProvider.setInternalNoticesEnabled(user.salesId, enabled),
    onSuccess: () => {
      refresh();
      // The profile switch appears only while the module is ON for the user.
      queryClient.invalidateQueries({ queryKey: ["internalNotices", "mine"] });
    },
    onError: () =>
      notify("crm.internalNotices.users.toggle_error", { type: "error" }),
  });
  const summary = useMutation({
    mutationFn: (value: boolean) =>
      dataProvider.setInternalNoticeGeneralSummary(user.salesId, value),
    onSuccess: refresh,
    onError: () =>
      notify("crm.internalNotices.users.summary_error", { type: "error" }),
  });
  // A generic message only: never the server text, a phone or a provider detail.
  const test = useMutation({
    mutationFn: () => dataProvider.sendInternalNoticeTest(user.salesId),
    onSuccess: () => notify("crm.internalNotices.test.success"),
    onError: () => notify("crm.internalNotices.test.error", { type: "error" }),
  });

  const switchId = `internal-notices-enabled-${user.salesId}`;
  const summaryId = `internal-notices-summary-${user.salesId}`;
  const projects = user.projects.join(", ");

  return (
    <li
      className="space-y-3 rounded-md border p-3"
      data-testid="internal-notice-user"
    >
      <div className="space-y-1">
        <p className="text-base font-semibold">{user.fullName}</p>
        <p className={HELP_CLASS}>
          {translate(`crm.internalNotices.roles.${user.role}`, {
            _: user.role,
          })}
          {projects ? ` · ${projects}` : ""}
        </p>
        <StatusPill
          tone={user.hasPhone ? "ok" : "not-ok"}
          text={translate(
            user.hasPhone
              ? "crm.internalNotices.users.phone_yes"
              : "crm.internalNotices.users.phone_no",
          )}
        />
      </div>
      <div className="flex min-h-11 items-center gap-3">
        <Switch
          id={switchId}
          checked={user.enabled}
          disabled={toggle.isPending || (!user.hasPhone && !user.enabled)}
          onCheckedChange={(value) => toggle.mutate(value)}
        />
        <Label htmlFor={switchId} className="text-[15px]">
          {translate("crm.internalNotices.users.switch_label", {
            name: user.fullName,
          })}
        </Label>
      </div>
      {!user.hasPhone ? (
        <p className={HELP_CLASS}>
          {translate("crm.internalNotices.users.no_phone_reason")}
        </p>
      ) : null}
      <div className="flex min-h-11 items-center gap-3">
        <Switch
          id={summaryId}
          checked={user.receivesGeneralSummary}
          disabled={summary.isPending}
          onCheckedChange={(value) => summary.mutate(value)}
        />
        <Label htmlFor={summaryId} className="text-[15px]">
          {translate("crm.internalNotices.users.general_summary_label", {
            name: user.fullName,
          })}
        </Label>
      </div>
      <Button
        type="button"
        variant="outline"
        className="min-h-11"
        disabled={test.isPending || !user.hasPhone}
        aria-label={translate("crm.internalNotices.test.button_for", {
          name: user.fullName,
        })}
        onClick={() => test.mutate()}
      >
        {translate(
          test.isPending
            ? "crm.internalNotices.test.sending"
            : "crm.internalNotices.test.button",
        )}
      </Button>
    </li>
  );
};

/**
 * Almikia Epic EN (TASK-091): the per-user list of the 'Avisos internos' screen. Every ACTIVE user
 * the provider returns gets the ON/OFF switch (default OFF), the 'recibe el resumen general'
 * marker and the test-notice button. A user WITHOUT a registered phone cannot be switched ON (the
 * reason is shown) and the number itself is never held by the browser: only `hasPhone` is. The
 * module-wide state reads 'Desactivado' until at least one user is ON. The list shows 100 users
 * at a time.
 */
export const InternalNoticeUsersCard = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [visible, setVisible] = useState(PAGE_SIZE);
  const users = useQuery({
    queryKey: USERS_QUERY_KEY,
    queryFn: () => dataProvider.getInternalNoticeUsers(),
    retry: false,
  });
  const enabledCount = (users.data ?? []).filter((user) => user.enabled).length;

  return (
    <Card data-testid="internal-notice-users-card">
      <CardContent className="space-y-3 p-4">
        <h2 className="text-lg font-semibold">
          {translate("crm.internalNotices.users.title")}
        </h2>
        <div role="status">
          <StatusPill
            tone={enabledCount > 0 ? "ok" : "not-ok"}
            text={
              enabledCount > 0
                ? translate("crm.internalNotices.settings.state_on", {
                    smart_count: enabledCount,
                  })
                : translate("crm.internalNotices.settings.state_off")
            }
          />
        </div>
        <p className={HELP_CLASS}>
          {translate("crm.internalNotices.users.help")}
        </p>
        {users.error ? (
          <ListErrorState
            message={translate("crm.internalNotices.users.load_error")}
            onRetry={() => users.refetch()}
          />
        ) : users.data === undefined ? (
          <ListCardsSkeleton rows={3} />
        ) : users.data.length === 0 ? (
          <p className={HELP_CLASS}>
            {translate("crm.internalNotices.users.empty")}
          </p>
        ) : (
          <>
            <ul className="space-y-3">
              {users.data.slice(0, visible).map((user) => (
                <UserRow key={String(user.salesId)} user={user} />
              ))}
            </ul>
            {users.data.length > visible ? (
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                onClick={() => setVisible((count) => count + PAGE_SIZE)}
              >
                {translate("crm.internalNotices.users.show_more")}
              </Button>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
};
